import type { ReactNode } from 'react';

// ============================================================================
// TIPOS
// ============================================================================

export interface ReportToolbarProps {
  /** Contenido del lado izquierdo (filtros) */
  leftContent?: ReactNode;
  /** Contenido del lado derecho (acciones) */
  rightContent?: ReactNode;
  /** Clase CSS adicional */
  className?: string;
}

export interface FilterSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  placeholder?: string;
  compact?: boolean;
}

export interface ActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'outline' | 'ghost';
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function ReportToolbar({ leftContent, rightContent, className = '' }: ReportToolbarProps) {
  return (
    <div
      className={`
        flex flex-col sm:flex-row sm:items-center sm:justify-between
        gap-3 sm:gap-4 mb-4
        ${className}
      `}
    >
      {/* Lado izquierdo - Filtros */}
      {leftContent && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">{leftContent}</div>
      )}

      {/* Lado derecho - Acciones */}
      {rightContent && <div className="flex items-center gap-2">{rightContent}</div>}
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTES
// ============================================================================

/**
 * Select compacto para filtros
 */
export function FilterSelect({
  id,
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder = 'Todos',
  compact = true,
}: FilterSelectProps) {
  return (
    <div className={compact ? 'flex items-center gap-2' : ''}>
      <label
        htmlFor={id}
        className={`
          text-xs font-medium text-gray-600 whitespace-nowrap
          ${compact ? '' : 'block mb-1'}
        `}
      >
        {label}:
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`
          px-2 py-1.5 text-sm
          bg-white border border-gray-300 rounded-lg
          focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          disabled:bg-gray-100 disabled:cursor-not-allowed
          min-w-[120px] max-w-[200px]
        `}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Checkbox compacto para filtros
 */
export function FilterCheckbox({
  id,
  label,
  checked,
  onChange,
  disabled = false,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      htmlFor={id}
      className={`
        inline-flex items-center gap-1.5 px-2 py-1.5
        text-xs font-medium rounded-lg
        cursor-pointer transition-colors
        ${
          checked
            ? 'bg-blue-50 text-blue-700 border border-blue-200'
            : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only"
      />
      <span
        className={`
          w-3 h-3 rounded flex items-center justify-center
          ${checked ? 'bg-blue-600' : 'bg-white border border-gray-300'}
        `}
      >
        {checked && (
          <svg className="w-2 h-2 text-white" viewBox="0 0 12 12" fill="currentColor">
            <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
          </svg>
        )}
      </span>
      {label}
    </label>
  );
}

/**
 * Botón de acción para la toolbar
 */
export function ActionButton({
  onClick,
  disabled = false,
  loading = false,
  variant = 'primary',
  icon,
  children,
  className = '',
}: ActionButtonProps) {
  const variantClasses = {
    primary: `
      bg-blue-600 text-white
      hover:bg-blue-700
      focus:ring-blue-500
    `,
    outline: `
      bg-white text-gray-700 border border-gray-300
      hover:bg-gray-50 hover:border-gray-400
      focus:ring-gray-500
    `,
    ghost: `
      bg-transparent text-gray-600
      hover:bg-gray-100
      focus:ring-gray-500
    `,
  }[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        inline-flex items-center justify-center gap-1.5
        px-3 py-1.5 text-sm font-medium rounded-lg
        focus:outline-none focus:ring-2 focus:ring-offset-1
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors
        ${variantClasses}
        ${className}
      `}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        icon
      )}
      <span className="hidden sm:inline">{children}</span>
    </button>
  );
}

/**
 * Separador visual para la toolbar
 */
export function ToolbarDivider() {
  return <div className="hidden sm:block w-px h-6 bg-gray-200" />;
}

/**
 * Botón para limpiar filtros
 */
export function ClearFiltersButton({
  onClick,
  visible = true,
}: {
  onClick: () => void;
  visible?: boolean;
}) {
  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
    >
      Limpiar filtros
    </button>
  );
}

// ============================================================================
// ICONOS
// ============================================================================

export const RefreshIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

export const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

export const FilterIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
    />
  </svg>
);

// ============================================================================
// EXPORTACIONES
// ============================================================================

export default ReportToolbar;
